import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../context/AppContext';
import { 
    FileText, Calendar, Filter, Download, 
    AlertCircle, DollarSign, TrendingDown 
} from 'lucide-react';
import * as XLSX from 'xlsx';

const FinancialSummary = () => {
    const { t, i18n } = useTranslation();
    const { authFetch } = useContext(AppContext);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Default to the current month
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const today = now.toISOString().split('T')[0];

    const [startDate, setStartDate] = useState(firstDay);
    const [endDate, setEndDate] = useState(today);

    const fetchReport = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await authFetch(`/api/financial_summary?start_date=${startDate}&end_date=${endDate}`);
            const result = await res.json();
            if (res.ok) {
                setData(result);
            } else {
                setError(result.error || "Failed to load financial summary");
            }
        } catch (err) {
            setError(err.message || "Network error occurred");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const exportToExcel = () => {
        if (!data) return;

        const wb = XLSX.utils.book_new();

        // 1. Executive Summary Sheet
        const summaryRows = [
            ["Metric", "Amount (CFA)"],
            ["Total Cash on Hand", data.summary.cash_balance],
            ["Owner Capital Balance", data.summary.capital_balance],
            ["Total Journal Debits", data.totals.debit],
            ["Total Journal Credits", data.totals.credit],
            ["", ""],
            ["Category Breakdown", "Subtotal Debit (CFA)", "Subtotal Credit (CFA)"]
        ];

        Object.entries(data.categories).forEach(([cat, catData]) => {
            summaryRows.push([cat, catData.subtotal_debit, catData.subtotal_credit]);
        });

        const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
        XLSX.utils.book_append_sheet(wb, wsSummary, "Executive Summary");

        // 2. Journal Sheet
        const wsJournal = XLSX.utils.json_to_sheet(data.journal.map(j => ({
            Date: j.date,
            Account: j.account_name,
            Debit: j.debit,
            Credit: j.credit,
            Description: j.entry_description
        })));
        XLSX.utils.book_append_sheet(wb, wsJournal, "Journal Entries");

        XLSX.writeFile(wb, `Financial_Report_${startDate}_to_${endDate}.xlsx`);
    };

    const isRtl = i18n.language === 'ar';

    if (loading && !data) return <div className="p-8 text-center text-gray-500 font-medium">{t('Loading Financial Report...')}</div>;

    return (
        <div className="space-y-6 pb-16 max-w-[1600px] mx-auto">
            {/* Header Area */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2.5">
                        <FileText className="w-6 h-6 text-indigo-600" />
                        {t('Financial Summary Report')}
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">
                        {t('Bilingual audit and category breakdown of double-entry ledger records.')}
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-3 bg-gray-50 p-2 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded border border-gray-200">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <input 
                            type="date" 
                            value={startDate} 
                            onChange={e => setStartDate(e.target.value)}
                            className="bg-transparent text-sm font-semibold text-gray-700 outline-none"
                        />
                    </div>
                    <span className="text-gray-400 text-sm font-bold">→</span>
                    <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded border border-gray-200">
                        <input 
                            type="date" 
                            value={endDate} 
                            onChange={e => setEndDate(e.target.value)}
                            className="bg-transparent text-sm font-semibold text-gray-700 outline-none"
                        />
                    </div>
                    <button 
                        onClick={fetchReport}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition flex items-center gap-1.5 shadow-sm"
                    >
                        <Filter className="w-4 h-4" /> {t('Apply')}
                    </button>
                    <button 
                        onClick={exportToExcel}
                        disabled={!data}
                        className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition flex items-center gap-1.5 shadow-sm disabled:opacity-50"
                    >
                        <Download className="w-4 h-4" /> Excel
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 p-4 rounded-lg flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-rose-500 shrink-0" />
                    <p className="font-medium text-sm">{error}</p>
                </div>
            )}

            {/* Executive Overview Section */}
            {data && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                            <span className="p-2.5 bg-indigo-50 text-indigo-600 rounded-lg">
                                <DollarSign className="w-5 h-5" />
                            </span>
                            <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded">
                                {t('Real-time Liquidity')}
                            </span>
                        </div>
                        <p className="text-gray-500 font-medium text-xs uppercase tracking-wider mb-1">{t('Total Cash on Hand')} | إجمالي النقد</p>
                        <h3 className="text-3xl font-bold text-gray-900 tracking-tight">
                            {data.summary.cash_balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            <span className="text-base font-normal ml-2 text-gray-500">CFA</span>
                        </h3>
                    </div>

                    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                            <span className="p-2.5 bg-emerald-50 text-emerald-600 rounded-lg">
                                <TrendingDown className="w-5 h-5 rotate-180" />
                            </span>
                            <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded">
                                {t('Stakeholder Equity')}
                            </span>
                        </div>
                        <p className="text-gray-500 font-medium text-xs uppercase tracking-wider mb-1">{t('Owner Money / Capital')} | رأس المال</p>
                        <h3 className="text-3xl font-bold text-gray-900 tracking-tight">
                            {data.summary.capital_balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            <span className="text-base font-normal ml-2 text-gray-500">CFA</span>
                        </h3>
                    </div>
                </div>
            )}

            {/* Critical Alerts Section */}
            {data?.alerts?.length > 0 && (
                <div className="bg-white rounded-xl border border-rose-200 overflow-hidden shadow-sm">
                    <div className="bg-rose-50 border-b border-rose-200 px-6 py-4 flex items-center justify-between">
                        <h3 className="text-rose-900 text-base font-bold flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-rose-600" />
                            {t('Audit Notices & Variances')}
                        </h3>
                        <span className="bg-rose-100 text-rose-800 px-2.5 py-0.5 rounded text-xs font-semibold">
                            {data.alerts.length} {t('Items')}
                        </span>
                    </div>
                    <div className="p-6 space-y-3">
                        {data.alerts.map((alert, idx) => (
                            <div key={idx} className="bg-rose-50/50 border border-rose-200/60 p-4 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="space-y-0.5 flex-1">
                                    <p className="text-sm font-semibold text-rose-900">{alert.message}</p>
                                    <p className="text-xs text-rose-700">{isRtl ? alert.arabic : alert.message}</p>
                                </div>
                                <span className="bg-rose-600 text-white px-2.5 py-1 rounded text-xs font-medium self-start md:self-center">
                                    {t('Action Required')}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* CATEGORY GROUPING SECTION */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 px-1">
                    <TrendingDown className="w-5 h-5 text-gray-600" />
                    <h3 className="text-lg font-bold text-gray-900">{t('Categorized Spending & Earnings')}</h3>
                </div>

                <div className="space-y-4">
                    {data && Object.entries(data.categories).map(([catName, catData]) => (
                        <div key={catName} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="p-4 bg-gray-50/80 border-b border-gray-200 flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <div className={`w-2.5 h-6 rounded-full ${catData.type === 'Expense' ? 'bg-rose-500' : 'bg-emerald-500'}`}></div>
                                    <div>
                                        <h4 className="text-base font-bold text-gray-900">{catName}</h4>
                                        <p className="text-xs text-gray-500 font-medium">{t(catData.type)}</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-gray-500 mb-0.5">{t('Subtotal (Debit)')} | الإجمالي (مدين)</p>
                                    <p className="text-lg font-bold text-indigo-700">
                                        {catData.subtotal_debit.toLocaleString(undefined, { minimumFractionDigits: 2 })} <span className="text-xs font-normal text-gray-500">CFA</span>
                                    </p>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead>
                                        <tr className="bg-white text-gray-500 text-xs border-b border-gray-100">
                                            <th className="py-3 px-4 w-32">{t('Date')}</th>
                                            <th className="py-3 px-4">{t('Description')} / البيان</th>
                                            <th className="py-3 px-4 text-right w-44">{t('Debit')} / مدين</th>
                                            <th className="py-3 px-4 text-right w-44">{t('Credit')} / دائن</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {catData.items.map((item, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50/60 transition-colors">
                                                <td className="py-2.5 px-4 text-xs font-mono text-gray-500">{item.date}</td>
                                                <td className="py-2.5 px-4 text-sm text-gray-800">{item.entry_description}</td>
                                                <td className="py-2.5 px-4 text-right font-medium text-rose-600">
                                                    {parseFloat(item.debit) > 0 ? parseFloat(item.debit).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}
                                                </td>
                                                <td className="py-2.5 px-4 text-right font-medium text-emerald-600">
                                                    {parseFloat(item.credit) > 0 ? parseFloat(item.credit).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-gray-50 text-gray-800 font-semibold text-xs border-t border-gray-200">
                                            <td colSpan="2" className="py-3 px-4 uppercase tracking-wider">{t('Category Subtotals')}</td>
                                            <td className="py-3 px-4 text-right text-sm text-rose-600">
                                                {catData.subtotal_debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="py-3 px-4 text-right text-sm text-emerald-600">
                                                {catData.subtotal_credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* JOURNAL ENTRIES SECTION */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 px-1">
                    <FileText className="w-5 h-5 text-gray-600" />
                    <h3 className="text-lg font-bold text-gray-900">{t('Complete Journal Entries Audit')}</h3>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="bg-gray-100 text-gray-700 text-xs font-semibold border-b border-gray-200">
                                    <th className="py-3 px-4 w-32">{t('Date')}</th>
                                    <th className="py-3 px-4">{t('Account')} / الحساب</th>
                                    <th className="py-3 px-4 text-right w-44">{t('Debit')} / مدين</th>
                                    <th className="py-3 px-4 text-right w-44">{t('Credit')} / دائن</th>
                                    <th className="py-3 px-4">{t('Description')} / الوصف</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data?.journal.map((j, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50/60 transition-colors">
                                        <td className="py-2.5 px-4 text-xs font-mono text-gray-500">{j.date}</td>
                                        <td className={`py-2.5 px-4 font-medium ${j.account_name.includes('Cash') || j.account_name.includes('Capital') ? 'text-indigo-700' : 'text-gray-800'}`}>
                                            {j.account_name}
                                            {j.account_name.includes('Cash') && <span className="ml-2 text-xs text-indigo-500">(Cash)</span>}
                                            {j.account_name.includes('Capital') && <span className="ml-2 text-xs text-indigo-500">(Capital)</span>}
                                        </td>
                                        <td className="py-2.5 px-4 text-right font-medium text-gray-900">
                                            {parseFloat(j.debit) > 0 ? parseFloat(j.debit).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}
                                        </td>
                                        <td className="py-2.5 px-4 text-right font-medium text-gray-500">
                                            {parseFloat(j.credit) > 0 ? parseFloat(j.credit).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}
                                        </td>
                                        <td className="py-2.5 px-4 text-gray-600 text-xs">{j.entry_description}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* GRAND TOTALS FOOTER */}
            {data && (
                <div className="bg-gray-900 text-white p-6 rounded-xl border border-gray-800 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div>
                        <h4 className="text-xl font-bold uppercase tracking-wider">{t('Grand Total Sums')}</h4>
                        <p className="text-gray-400 text-xs mt-1">{t('Accounting balance verification (Debit = Credit)')}</p>
                    </div>
                    <div className="flex flex-wrap gap-4">
                        <div className="bg-gray-800 border border-gray-700 p-4 rounded-lg min-w-[200px]">
                            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-1">{t('Total Debit')} | مدين</p>
                            <p className="text-2xl font-bold text-rose-400">
                                {data.totals.debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </p>
                        </div>
                        <div className="bg-gray-800 border border-gray-700 p-4 rounded-lg min-w-[200px]">
                            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-1">{t('Total Credit')} | دائن</p>
                            <p className="text-2xl font-bold text-emerald-400">
                                {data.totals.credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinancialSummary;
